import React, { useEffect, useId, useRef } from 'react';
import Icon from './Icon';

export function Button({ variant, busy, children, className = '', disabled, type = 'button', ...props }) {
  const appearance = variant || className.match(/\bbtn-(primary|secondary|ghost|danger|warning|success)\b/)?.[1] || 'secondary';
  return <button type={type} className={`btn btn-${appearance} ${className}`} disabled={disabled || busy}
    aria-busy={busy || undefined} {...props}>{busy && <span className="spinner" aria-hidden="true" />}{children}</button>;
}

export function IconButton({ icon, label, className = '', ...props }) {
  return <Button variant="ghost" className={`icon-button ${className}`} aria-label={label} title={label} {...props}><Icon name={icon} /></Button>;
}

export function Tabs({ items, value, onChange, label, variant = 'line', className = '' }) {
  const id = useId();
  const refs = useRef([]);
  const selectNext = (event, index) => {
    const keys = { ArrowRight: (index + 1) % items.length, ArrowLeft: (index + items.length - 1) % items.length, Home: 0, End: items.length - 1 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const next = keys[event.key];
    if (items[next].key !== value && onChange(items[next].key) === false) return;
    refs.current[next]?.focus();
  };
  return <div className={`tabs tabs-${variant} ${className}`} role="tablist" aria-label={label}>
    {items.map((item, index) => <button type="button" role="tab" key={item.key} id={`${id}-${item.key}`}
      ref={el => { refs.current[index] = el; }} aria-selected={value === item.key}
      tabIndex={value === item.key ? 0 : -1} className={`tab ${value === item.key ? 'active' : ''}`}
      onClick={() => { if (item.key !== value) onChange(item.key); }} onKeyDown={event => selectNext(event, index)}>{item.label}</button>)}
  </div>;
}

export function FormField({ label, hint, error, children, className = '' }) {
  const id = useId();
  const child = React.Children.only(children);
  const inputId = child.props.id || id;
  return <div className={`form-group ${className}`}>
    <label className="form-label" htmlFor={inputId}>{label}</label>
    {React.cloneElement(child, { id: inputId, 'aria-invalid': error ? true : undefined,
      'aria-describedby': error || hint ? `${id}-help` : undefined })}
    {(hint || error) && <p id={`${id}-help`} className={error ? 'field-error' : 'form-hint'}>{error || hint}</p>}
  </div>;
}

export function EmptyState({ title, children, action, icon = 'spark' }) {
  return <div className="empty-state"><span className="empty-state-icon"><Icon name={icon} size={28} /></span>
    <h2>{title}</h2>{children && <p>{children}</p>}{action}</div>;
}

export function Dialog({ title, children, onClose, className = '' }) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const id = useId();
  useEffect(() => {
    const element = ref.current;
    const previous = document.activeElement;
    element.showModal();
    const cancel = event => { event.preventDefault(); closeRef.current(); };
    const trapFocus = event => {
      if (event.key !== 'Tab') return;
      const controls = [...element.querySelectorAll('button, [href], input, select, textarea, [tabindex]')]
        .filter(control => !control.disabled && control.tabIndex >= 0 && control.getClientRects().length);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); element.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !element.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !element.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    };
    element.addEventListener('cancel', cancel);
    element.addEventListener('keydown', trapFocus);
    return () => {
      element.removeEventListener('cancel', cancel);
      element.removeEventListener('keydown', trapFocus);
      element.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);
  return <dialog ref={ref} className={`ui-dialog ${className}`} aria-labelledby={id} onClick={event => {
    if (event.target === event.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onClose();
    }
  }}>
    <div className="dialog-header"><h2 id={id}>{title}</h2><IconButton icon="close" label="Закрыть диалог" onClick={onClose} /></div>
    <div className="dialog-body">{children}</div>
  </dialog>;
}

export { default as Icon } from './Icon';
